import { CountUp, CountUpOptions } from 'countup.js'
import { DirectiveBinding, ObjectDirective } from 'vue'

type CountUpElement = HTMLElement & { __countup_instance__?: CountUp }

const defaultOptions: CountUpOptions = {
    startVal: 0,
    decimalPlaces: 0,
    duration: 2,
    separator: ',',
}

function createCountUp(el: HTMLElement, value: number | null, options?: Partial<CountUpOptions>) {
    const opts: CountUpOptions = { ...defaultOptions, ...options } as CountUpOptions
    // If value is null treat it as 0 for initialization; update will set to actual value
    const endVal = typeof value === 'number' ? value : 0
    return new CountUp(el, endVal, opts)
}

const CountUpDirective: ObjectDirective = {
    mounted(el: CountUpElement, binding: DirectiveBinding<number | CountUpOptions>) {
        // binding.value may be a number to animate to, or an options object with `endVal`/other keys
        let value: number | null = null
        let options: Partial<CountUpOptions> | undefined

        if (typeof binding.value === 'number') {
            value = binding.value
        } else if (binding.value && typeof binding.value === 'object') {
            // If user passed CountUpOptions and included start/end values, respect them.
            options = binding.value as Partial<CountUpOptions>
            // Try to read an endVal property if present (some use endVal)
            // @ts-ignore
            if (typeof binding.value.value === 'number') value = (binding.value as any).value
            //value = options.value
        }

        const instance = createCountUp(el, value, options)
        // store on element for reuse
        el.__countup_instance__ = instance

        // if initial value provided as number, start animation
        if (typeof value === 'number') {
            instance.start()
        } else {
            // start with 0 or provided startVal
            instance.start()
        }
    },

    updated(el: CountUpElement, binding: DirectiveBinding<number | CountUpOptions>) {
        const inst = el.__countup_instance__

        // If no instance present, create one
        if (!inst) {
            // Reuse mounted logic to init by calling the mounted handler directly
            CountUpDirective.mounted?.(el as any, binding as any, undefined, undefined)
            return
        }

        // Determine new desired value and possible new options
        let newValue: number | null = null
        let newOptions: Partial<CountUpOptions> | undefined

        if (typeof binding.value === 'number') {
            newValue = binding.value
        } else if (binding.value && typeof binding.value === 'object') {
            newOptions = binding.value as Partial<CountUpOptions>
            // @ts-ignore
            if (typeof newOptions.value === 'number') newValue = newOptions.value
        }

        // If options changed, recreate instance
        // Simple check: compare JSON of options (acceptable for small option sets)
        if (newOptions) {
            const oldOpts = (inst as any).options ? JSON.stringify((inst as any).options) : null
            const incoming = JSON.stringify({ ...defaultOptions, ...newOptions })
            if (oldOpts !== incoming) {
                // recreate
                const newInst = createCountUp(el, newValue, newOptions)
                el.__countup_instance__ = newInst
                if (typeof newValue === 'number') newInst.start()
                return
            }
        }

        // If just a number changed, update value
        if (typeof newValue === 'number') {
            inst.update(newValue)
        }
    },

    unmounted(el: CountUpElement) {
        // cleanup
        if (el.__countup_instance__) {
            try {
                // CountUp doesn't expose a destroy; remove reference so GC can collect
                // @ts-ignore
                delete el.__countup_instance__
            } catch (e) {
                console.error(e)
            }
        }
    },
}

export default CountUpDirective
